import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Text } from '@dropbox/dig-components/typography';
import { Box } from '@dropbox/dig-foundations';
import {
  AnalyticsLine,
  BoxViewLine,
  ComputerLine,
  FileTemplateLine,
  LinkLine,
  MediaFullWidthLine,
  NotSignedLine,
  PersonMultipleLine,
  SendLine,
  SignatureLine,
  Twinkle2Line,
} from '@dropbox/dig-icons/assets';
import { UIIcon } from '@dropbox/dig-icons';

import { Plan } from 'hellospa/page/payment/types';

type HighlightsProps = {
  isMinimal?: boolean;
  plan: Partial<Plan>;
};

const Highlight = ({
  icon,
  isMinimal,
  children,
}: React.PropsWithChildren<{
  icon: React.ComponentType<React.SVGAttributes<SVGElement>>;
  isMinimal: boolean;
}>) => {
  return (
    <Box
      display="flex"
      alignItems="flex-start"
      flexGrow={1}
      flexShrink={1}
      style={{
        flexBasis: `calc(${isMinimal ? '50%' : '100%'} - 12px)`,
      }}
    >
      <Box marginRight="Micro Small">
        <UIIcon src={icon} />
      </Box>
      <Text variant="paragraph">{children}</Text>
    </Box>
  );
};

const Highlights = ({ isMinimal = false, plan }: HighlightsProps) => {
  return (
    <Box
      display="flex"
      flexWrap="wrap"
      flexDirection={{ xsmall: 'column', medium: isMinimal ? 'row' : 'column' }}
      style={{ gap: '12px' }}
      data-qa-ref="highlights"
      data-testid="highlights"
    >
      {!plan.is_api && plan.is_team_plan && (
        <Highlight isMinimal={isMinimal} icon={PersonMultipleLine}>
          <FormattedMessage
            id=""
            description="Paid plan feature name; Shown next ot a checkmark icon which indicates that the plan has some number of people included. People count is always greater or equal than 2."
            defaultMessage="{minNumUsers, number}+ people"
            values={{
              minNumUsers: 2,
            }}
          />
        </Highlight>
      )}

      <Highlight isMinimal={isMinimal} icon={FileTemplateLine}>
        {plan.num_templates === null ? (
          <FormattedMessage
            id=""
            description="Text indicating how many signature request templates the given plan has."
            defaultMessage="Unlimited templates"
          />
        ) : (
          <FormattedMessage
            id=""
            description="Text indicating how many signature request templates the given plan has."
            defaultMessage="{num_templates, plural, one {# Template} other {# Templates}}"
            values={{
              num_templates: plan.num_templates,
            }}
          />
        )}
      </Highlight>

      {plan.features && plan.features.branding && (
        <Highlight isMinimal={isMinimal} icon={Twinkle2Line}>
          <FormattedMessage
            id=""
            description="Paid plan feature name; Shown next to a checkmark icon which indicates that the plan has this feature available. Ex. '✓ Branding'"
            defaultMessage="Branding"
          />
        </Highlight>
      )}

      {plan.features &&
        plan.features.data_validation &&
        !(!plan.is_api && plan.is_team_plan) && (
          <Highlight isMinimal={isMinimal} icon={AnalyticsLine}>
            <FormattedMessage
              id=""
              description="Paid plan feature name; Shown next to a checkmark icon which indicates that the plan has this feature available. Ex. '✓ Data validation'"
              defaultMessage="Data validation"
            />
          </Highlight>
        )}
      {!plan.is_api && !plan.is_team_plan && (
        <Highlight isMinimal={isMinimal} icon={NotSignedLine}>
          <FormattedMessage
            id=""
            description="Paid plan feature name; Shown next to a checkmark icon which indicates that the plan has this feature available. Ex. '✓ Signer fields'"
            defaultMessage="Signer fields"
          />
        </Highlight>
      )}
      {plan.is_api && (
        <Highlight isMinimal={isMinimal} icon={MediaFullWidthLine}>
          <FormattedMessage
            id=""
            description="Paid plan feature name; Shown next to a checkmark icon which indicates that the plan has this feature available. Ex. '✓ API dashboard'"
            defaultMessage="API dashboard"
          />
        </Highlight>
      )}
      {plan.is_api && plan.features && plan.features.embedded_signing && (
        <Highlight isMinimal={isMinimal} icon={BoxViewLine}>
          <FormattedMessage
            id=""
            description="Paid plan feature name; Shown next to a checkmark icon which indicates that the plan has this feature available. Ex. '✓ Embedded signing'"
            defaultMessage="Embedded signing"
          />
        </Highlight>
      )}
      {plan.is_api && (
        <Highlight isMinimal={isMinimal} icon={ComputerLine}>
          <FormattedMessage
            id=""
            description="Paid plan feature name; Shown next to a checkmark icon which indicates that the plan has this feature available. Ex. '✓ SDKs in 6 languages'"
            defaultMessage="SDKs in 6 languages"
          />
        </Highlight>
      )}
      {!plan.is_team_plan && plan.features && plan.features.template_links && (
        <Highlight isMinimal={isMinimal} icon={LinkLine}>
          <FormattedMessage
            id=""
            description="Paid plan feature name; Shown next to a checkmark icon which indicates that the plan has this feature available. Ex. '✓ Template links'"
            defaultMessage="Template links"
          />
        </Highlight>
      )}
      {!plan.is_api && (
        <Highlight isMinimal={isMinimal} icon={SignatureLine}>
          {/*
              If the user is buying a plan and ends up on the payment
              page, then num_sig_requests will always be null, be we
              handle the case that it's a finite number just in
              case.
            */}
          {plan.num_sig_requests === null ? (
            <FormattedMessage
              id=""
              description="Text indicating the number of signature requests the given plan has."
              defaultMessage="Unlimited signatures"
            />
          ) : (
            <FormattedMessage
              id=""
              description="Text indicating the number of signature requests the given plan has."
              defaultMessage="{num_sig_requests, plural, one {# signature request} other {# signature requests}} per month"
              values={{
                num_sig_requests: plan.num_sig_requests,
              }}
            />
          )}
        </Highlight>
      )}

      {!plan.is_api && plan.features && plan.features.bulk_send && (
        <Highlight isMinimal={isMinimal} icon={SendLine}>
          <FormattedMessage
            id=""
            description="Paid plan feature name; Shown next to a checkmark icon which indicates that the plan has this feature available. Ex. '✓ Bulk send'"
            defaultMessage="Bulk send"
          />
        </Highlight>
      )}
    </Box>
  );
};

export default Highlights;
